/**
 * DT1 Simply Menu — API Service
 * Communique avec le backend Node.js/Express qui tourne sur la VM
 * Configurer API_BASE_URL selon votre environnement
 */

// Configuration de l'API
// En développement : http://localhost:3001
// En production (même domaine) : /api (Nginx reverse proxy)
// En production (domaine différent) : https://api.votre-domaine.com
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ApiAliment {
  id: number;
  nom: string;
  categorie: string;
  image: string;
  description: string;
  glucides_pour_100g: number;
  calories_100g: number;
  fibres_100g: number;
  index_glycemique: number;
  // Nouveaux champs nutritionnels détaillés
  proteines_100g?: number;
  lipides_100g?: number;
  sucres_100g?: number;
  charge_glycemique?: number;
  nutriscore?: string;
  aliases?: string[];
  source?: string;
  score_confiance?: number;
}

export interface ApiIngredient {
  nom: string;
  quantite: number;
  unite: string;
  glucides: number;
}

export interface ApiPlat {
  id: number;
  nom: string;
  categorie: string;
  image: string;
  resume: string;
  nutri_score: string;
  glucides_totaux: number;
  calories: number;
  ingredients: ApiIngredient[];
  etapes: string[];
  conseil?: string;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  message?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('dt1_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function request<T>(
  path: string,
  options: { method?: string; body?: string; headers?: Record<string, string> } = {}
): Promise<T> {
  const baseUrl = API_BASE_URL || '/api';
  const url = `${baseUrl}${path}`;

  // eslint-disable-next-line no-console
  console.log(`[API] ${options.method ?? 'GET'} ${url}`, { hasAuth: !!getAuthHeaders().Authorization });

  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(options.headers as Record<string, string> ?? {}),
    },
    ...options,
  });

  // eslint-disable-next-line no-console
  console.log(`[API] Response ${res.status} for ${options.method ?? 'GET'} ${url}`);

  if (!res.ok) {
    const errText = await res.text().catch(() => `Erreur HTTP ${res.status}`);
    // eslint-disable-next-line no-console
    console.error(`[API] Error body:`, errText);
    let errMsg = errText;
    try {
      const errJson = JSON.parse(errText);
      errMsg = errJson.message ?? errText;
    } catch { /* pas du JSON */ }
    throw new Error(errMsg ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

export const apiHealthCheck = async (): Promise<{ ok: boolean; url: string; error?: string }> => {
  const baseUrl = API_BASE_URL || '/api';
  const url = `${baseUrl}/api/health`;
  try {
    const res = await fetch(url, { method: 'GET' });
    if (res.ok) {
      const data = await res.json();
      return { ok: true, url, error: undefined };
    }
    return { ok: false, url, error: `HTTP ${res.status}` };
  } catch (e) {
    return { ok: false, url, error: e instanceof Error ? e.message : 'Erreur réseau' };
  }
};

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const apiLogin = async (login: string, password: string): Promise<boolean> => {
  try {
    const data = await request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    });
    if (data.success && data.token) {
      localStorage.setItem('dt1_admin_token', data.token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

export const apiLogout = (): void => {
  localStorage.removeItem('dt1_admin_token');
};

export const isApiAuthenticated = (): boolean => {
  return !!localStorage.getItem('dt1_admin_token');
};

// ─── Aliments ─────────────────────────────────────────────────────────────────

export const fetchAllAliments = (): Promise<ApiAliment[]> =>
  request<ApiAliment[]>('/api/aliments');

export const createAliment = (data: Omit<ApiAliment, 'id'>): Promise<ApiAliment> =>
  request<ApiAliment>('/api/aliments', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateAliment = (id: number, data: Omit<ApiAliment, 'id'>): Promise<ApiAliment> =>
  request<ApiAliment>(`/api/aliments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteAliment = (id: number): Promise<void> =>
  request<void>(`/api/aliments/${id}`, { method: 'DELETE' });

// ─── Plats ────────────────────────────────────────────────────────────────────

export const fetchAllPlats = (): Promise<ApiPlat[]> =>
  request<ApiPlat[]>('/api/plats');

export const createPlat = (data: Omit<ApiPlat, 'id'>): Promise<ApiPlat> =>
  request<ApiPlat>('/api/plats', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const deletePlat = (id: number): Promise<void> =>
  request<void>(`/api/plats/${id}`, { method: 'DELETE' });